/**
 * CommandPalette — Cmd+K / Ctrl+K global command bar.
 *
 * Ported from BB-Terminal's `CommandBar.tsx` + `functions.ts`:
 * type `RELIANCE` to jump to the stock, `RELIANCE INTEL` to the
 * scorecard tab, `RELIANCE GP` / `CHART` to the candle chart, etc.
 *
 * Grammar
 *   <SYMBOL>                     → company page (Overview tab)
 *   <SYMBOL> <FUNC>              → function applied to symbol
 *   <FUNC>                       → symbol-less function (HOME, AI, SIGNALS, …)
 *
 * Function codes (India-appropriate subset for v1)
 *   INTEL         — rule-based scorecard
 *   GP / CHART    — candlestick chart
 *   FA            — financials
 *   KEY           — ratios / key metrics
 *   NI            — BSE notices & corporate notices
 *   PR / PITCH    — pitch report generator
 *   HOME          — home dashboard
 *   AI            — AI research chat
 *   SIGNALS       — signal center
 *   RESEARCH      — screener
 *   CLIENTS       — advisor clients
 *   BILLING       — billing & credits
 *   HELP          — show this reference
 *
 * Keyboard
 *   Cmd/Ctrl+K        — open
 *   /                  — open when no input is focused
 *   ↑ / ↓             — walk history / highlighted option
 *   Tab               — autocomplete the current function code
 *   Enter / <GO>      — execute
 *   Esc               — close
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Terminal, ArrowRight, X, Keyboard } from 'lucide-react';

// ── Function registry ──────────────────────────────────────

const FUNCTIONS = [
  { code: 'INTEL',    name: 'INTEL Scorecard',     needsSymbol: true,  group: 'Security', summary: 'Bull/bear signals across technical, valuation, fundamentals, flow' },
  { code: 'CHART',    name: 'Candlestick Chart',   needsSymbol: true,  group: 'Security', summary: 'Daily candles + volume, 1M · 3M · 6M · 1Y · 3Y · 5Y' },
  { code: 'GP',       name: 'Chart (alias)',       needsSymbol: true,  group: 'Security', summary: 'Same as CHART — BB-Terminal alias' },
  { code: 'FA',       name: 'Financials',          needsSymbol: true,  group: 'Security', summary: 'Revenue, EBITDA, PAT, EPS history' },
  { code: 'KEY',      name: 'Key Metrics',         needsSymbol: true,  group: 'Security', summary: 'PE, EV/EBITDA, ROE, margins' },
  { code: 'NI',       name: 'Notices',             needsSymbol: true,  group: 'Security', summary: 'BSE notices + corporate actions' },
  { code: 'PR',       name: 'Pitch Report',        needsSymbol: true,  group: 'Security', summary: 'Generate AI pitch report for this stock' },
  { code: 'PITCH',    name: 'Pitch Report (alias)', needsSymbol: true, group: 'Security', summary: 'Alias for PR' },

  { code: 'HOME',     name: 'Home / Dashboard',    needsSymbol: false, group: 'System',   summary: 'Go to the home dashboard' },
  { code: 'AI',       name: 'AI Research Chat',    needsSymbol: false, group: 'System',   summary: 'Open the KubAIr research agent' },
  { code: 'SIGNALS',  name: 'Signal Center',       needsSymbol: false, group: 'System',   summary: 'Proactive market signal feed' },
  { code: 'RESEARCH', name: 'Research / Screener', needsSymbol: false, group: 'System',   summary: 'Filter stocks by fundamentals or flows' },
  { code: 'SCAN',     name: 'TA Scanners',         needsSymbol: false, group: 'Market',   summary: 'Golden cross, RSI extremes, breakouts and more' },
  { code: 'NIFTY',    name: 'Nifty 50 TA',         needsSymbol: false, group: 'Market',   summary: 'Nifty 50 chart + indicators' },
  { code: 'SENSEX',   name: 'Sensex TA',           needsSymbol: false, group: 'Market',   summary: 'Sensex chart + indicators' },
  { code: 'BANKNIFTY', name: 'Bank Nifty TA',      needsSymbol: false, group: 'Market',   summary: 'Bank Nifty chart + indicators' },
  { code: 'BREADTH',  name: 'Market Breadth',      needsSymbol: false, group: 'Market',   summary: 'Advance/decline + % above 50/200 DMA' },
  { code: 'CLIENTS',  name: 'Clients',             needsSymbol: false, group: 'System',   summary: 'Advisor client book' },
  { code: 'BILLING',  name: 'Billing & Credits',   needsSymbol: false, group: 'System',   summary: 'Plans, top-ups and usage' },
  { code: 'HELP',     name: 'Function Directory',  needsSymbol: false, group: 'System',   summary: 'List every KubAIr function code' },
];

const FN_BY_CODE = Object.fromEntries(FUNCTIONS.map((f) => [f.code, f]));

// Map a parsed command to a navigation target.
function pathForCommand(symbol, code) {
  const sym = symbol && symbol.toUpperCase();
  switch (code) {
    case 'HOME':     return '/public/home';
    case 'AI':       return '/public/ai';
    case 'SIGNALS':  return '/public/signals';
    case 'RESEARCH': return '/public/screener';
    case 'SCAN':     return '/public/scanners';
    case 'NIFTY':    return '/public/index-ta/NIFTY_50';
    case 'SENSEX':   return '/public/index-ta/SENSEX';
    case 'BANKNIFTY': return '/public/index-ta/BANK_NIFTY';
    case 'BREADTH':  return '/public/index-ta?breadth=1';
    case 'CLIENTS':  return '/public/clients';
    case 'BILLING':  return '/settings/billing';
    case 'HELP':     return '__help__';

    case 'INTEL':    return `/public/india-company/${sym}?tab=intel`;
    case 'CHART':
    case 'GP':       return `/public/india-company/${sym}?tab=chart`;
    case 'FA':       return `/public/india-company/${sym}?tab=financials`;
    case 'KEY':      return `/public/india-company/${sym}?tab=overview`;
    case 'NI':       return `/public/india-company/${sym}?tab=bse-notices`;
    case 'PR':
    case 'PITCH':    return `/public/india-company/${sym}/pitch`;
    default:         return `/public/india-company/${sym}`;
  }
}


/** Parse "RELIANCE", "RELIANCE INTEL", "INTEL RELIANCE", "HOME". */
function parseCommand(raw) {
  const parts = (raw || '').trim().toUpperCase().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return null;

  if (parts.length === 1) {
    const p = parts[0];
    if (FN_BY_CODE[p]) {
      const fn = FN_BY_CODE[p];
      if (fn.needsSymbol) return { error: `${p} needs a symbol. Try: RELIANCE ${p}` };
      return { code: p };
    }
    // Just a symbol — default target is the company page.
    return { symbol: p, code: 'SYMBOL' };
  }

  const [a, b] = parts;
  if (FN_BY_CODE[b]) return { symbol: a, code: b };
  if (FN_BY_CODE[a]) {
    const fn = FN_BY_CODE[a];
    return fn.needsSymbol ? { symbol: b, code: a } : { code: a };
  }
  return { error: `Unknown command — try HELP` };
}


// ── Palette component ───────────────────────────────────────

export default function CommandPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [err, setErr] = useState(null);
  const [sugIdx, setSugIdx] = useState(0);
  const [history, setHistory] = useState([]);
  const [histIdx, setHistIdx] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const inputRef = useRef(null);

  // Global open/close hotkey + CustomEvent bridge (for top-bar button, etc.)
  useEffect(() => {
    const handler = (e) => {
      const isModK = (e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K');
      const isSlash = e.key === '/' && !isInputFocused();
      if (isModK) {
        e.preventDefault();
        setOpen((o) => !o);
        return;
      }
      if (isSlash) {
        e.preventDefault();
        setOpen(true);
        return;
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    const openEvent = () => setOpen(true);
    window.addEventListener('keydown', handler);
    window.addEventListener('kubair:open-command-palette', openEvent);
    return () => {
      window.removeEventListener('keydown', handler);
      window.removeEventListener('kubair:open-command-palette', openEvent);
    };
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 0);
    else { setInput(''); setErr(null); setHistIdx(null); setHelpOpen(false); }
  }, [open]);

  const tokens = input.trim().toUpperCase().split(/\s+/).filter(Boolean);
  const last = tokens[tokens.length - 1] ?? '';

  const suggestions = useMemo(() => {
    if (!input) return [];
    return FUNCTIONS.filter((f) => f.code.startsWith(last)).slice(0, 8);
  }, [input, last]);

  const run = useCallback(() => {
    const raw = input.trim();
    if (!raw) return;
    const parsed = parseCommand(raw);
    if (!parsed) return;
    if (parsed.error) { setErr(parsed.error); return; }

    const target = pathForCommand(parsed.symbol, parsed.code);
    setHistory((h) => [raw.toUpperCase(), ...h.filter((x) => x !== raw.toUpperCase())].slice(0, 20));
    setHistIdx(null);
    if (target === '__help__') { setHelpOpen(true); setInput(''); return; }
    setOpen(false);
    navigate(target);
  }, [input, navigate]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      run();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (history.length === 0) return;
      const next = histIdx == null ? 0 : Math.min(history.length - 1, histIdx + 1);
      setHistIdx(next); setInput(history[next]);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (histIdx == null) return;
      const next = histIdx - 1;
      if (next < 0) { setHistIdx(null); setInput(''); }
      else { setHistIdx(next); setInput(history[next]); }
    } else if (e.key === 'Tab' && suggestions.length > 0) {
      e.preventDefault();
      const rest = tokens.slice(0, -1);
      setInput([...rest, suggestions[sugIdx]?.code].join(' ') + ' ');
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-start justify-center bg-black/60 backdrop-blur-sm pt-[14vh] px-4"
      onClick={() => setOpen(false)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl rounded-xl border border-border bg-popover shadow-2xl overflow-hidden"
      >
        {/* Top input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
          <Terminal className="w-4 h-4 text-emerald-500 shrink-0" />
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">CMD</span>
          <span className="text-emerald-500 font-bold">{'>'}</span>
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => { setInput(e.target.value); setErr(null); setSugIdx(0); }}
            onKeyDown={onKeyDown}
            placeholder="RELIANCE   |   RELIANCE INTEL   |   HDFCBANK CHART   |   HELP   |   HOME"
            spellCheck={false}
            autoCapitalize="characters"
            className="flex-1 bg-transparent uppercase text-foreground placeholder:text-muted-foreground/70 focus:outline-none text-sm tracking-wide font-mono"
          />
          <button
            onClick={run}
            className="text-emerald-500 hover:text-emerald-400 text-xs font-bold border border-emerald-500/40 px-2 py-0.5 rounded"
            title="Execute (Enter)"
          >
            &lt;GO&gt;
          </button>
          <button
            onClick={() => setOpen(false)}
            className="p-1 rounded hover:bg-muted text-muted-foreground"
            title="Close (Esc)"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Error line */}
        {err && (
          <div className="px-4 py-2 bg-red-500/5 border-b border-border text-[12px] text-red-500">
            {err}
          </div>
        )}

        {/* Suggestions */}
        {!helpOpen && suggestions.length > 0 && (
          <div className="max-h-[40vh] overflow-y-auto">
            {suggestions.map((f, i) => (
              <button
                key={f.code}
                onMouseEnter={() => setSugIdx(i)}
                onClick={() => {
                  const rest = tokens.slice(0, -1);
                  setInput([...rest, f.code].join(' ') + ' ');
                  inputRef.current?.focus();
                }}
                className={`w-full text-left flex items-baseline gap-3 px-4 py-2 text-[13px] ${
                  i === sugIdx ? 'bg-emerald-500/10' : ''
                }`}
              >
                <span className="text-emerald-500 font-bold w-16 shrink-0 font-mono">{f.code}</span>
                <span className="flex-1 min-w-0 truncate text-foreground">{f.name}</span>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{f.group}</span>
              </button>
            ))}
          </div>
        )}

        {/* Help panel */}
        {helpOpen && (
          <div className="max-h-[60vh] overflow-y-auto px-4 py-3 text-sm">
            <p className="text-[11px] text-muted-foreground mb-2">
              Every code accepts a symbol prefix. Example: <code className="text-emerald-500 font-mono">HDFCBANK INTEL</code>.
            </p>
            <div className="grid md:grid-cols-2 gap-x-6">
              {FUNCTIONS.map((f) => (
                <div key={f.code} className="flex items-start gap-3 py-1.5 border-b border-border/40 last:border-0">
                  <span className="text-emerald-500 font-bold font-mono w-20 shrink-0 text-[12px]">{f.code}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-medium">{f.name}</div>
                    <div className="text-[11px] text-muted-foreground">{f.summary}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer: hotkey hints */}
        <div className="px-4 py-2 border-t border-border flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          <div className="flex items-center gap-3">
            <Keyboard className="w-3 h-3" />
            <span>↑↓ history</span>
            <span>⇥ autocomplete</span>
            <span>⏎ run</span>
          </div>
          <div className="flex items-center gap-1">
            <span>KubAIr terminal</span>
            <ArrowRight className="w-3 h-3" />
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}


function isInputFocused() {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable;
}
